import * as React from 'react';

import DeliverableMediaRow from '../../parts/DeliverableMediaRow';
import DeliverableMediaLink from '../../parts/DeliverableMediaLink';
import DeliverableMediaAttachments from '../../parts/DeliverableMediaAttachments';
import DeliverableMediaUploader from '../../parts/DeliverableMediaUploader';
import DeliverableMediaPostForm from '../../parts/DeliverableMediaPostForm';
import DeliverableMediaRowContextMenu from '../../parts/DeliverableMediaRowContextMenu';

import { __t, applyTranslations } from 'utils/language';
import { normalizeUrl } from 'utils/url';
import { isUrl } from 'utils/validators';
import {
  DELIVERABLE_ATTACHMENT_TYPE_HIGHRES,
  filterAttachmentsByType
} from 'utils/deliverables';

import type {
  Deliverable,
  DeliverableAttachment,
  DeliverableUploadedEventAction,
  Subcampaign
} from 'types';

applyTranslations(require('./deliverable-media-other.i18n.json'));

type Props = {
  canUpload: boolean,
  subcampaign: Subcampaign,
  deliverable: Deliverable,
  onDeletePost: () => void,
  onDeleteAttachment: (a: DeliverableAttachment) => void,
  onDownloadAttachments: (a?: string | null) => void,
  onUploadModeToggled?: () => void,
  onTrackDeliverableUploadEvent: (eventAction: DeliverableUploadedEventAction) => void
};

const msgKey = 'deliverable_media_other';

class DeliverableMediaOther extends React.PureComponent<Props> {
  handleUploadTracking = (eventAction: DeliverableUploadedEventAction): void => {
    const { onTrackDeliverableUploadEvent, onUploadModeToggled } = this.props;

    onTrackDeliverableUploadEvent(eventAction);

    if (onUploadModeToggled) {
      onUploadModeToggled();
    }
  };

  renderForm(): React.ReactNode {
    const { canUpload, deliverable, subcampaign, onDownloadAttachments, onUploadModeToggled } = this.props;

    return (
      <DeliverableMediaRow
        canUpload={canUpload}
        title={__t(`${msgKey}.labels.url`)}
        comment={__t(`${msgKey}.messages.url`)}
        status="ok"
        contextMenu={(
          <DeliverableMediaRowContextMenu
            subcampaign={subcampaign}
            deliverable={deliverable}
            canUpload={canUpload}
            onDownloadAttachments={onDownloadAttachments}
            onUploadModeToggled={onUploadModeToggled}
          />
        )}
        content={(
          <DeliverableMediaPostForm
            canUpload={canUpload}
            offerId={subcampaign.offer.id}
            deliverableId={deliverable.id}
            placeholder="https://example.com"
            postUrlValidator={isUrl}
            normalizePostUrl={normalizeUrl}
            onUploadModeTracking={() => this.handleUploadTracking('other url')}
          />
        )}
      />
    );
  }

  render(): React.ReactNode {
    const {
      canUpload,
      subcampaign,
      deliverable,
      onDeletePost,
      onDeleteAttachment,
      onDownloadAttachments,
      onUploadModeToggled
    } = this.props;

    const post = deliverable.post;
    const attachments = deliverable.postAttachments || [];
    const attachmentsHighres = filterAttachmentsByType(attachments, DELIVERABLE_ATTACHMENT_TYPE_HIGHRES);

    return (
      <React.Fragment>
        {
          post ?
            <DeliverableMediaRow
              canUpload={canUpload}
              title={__t(`${msgKey}.labels.url`)}
              comment={__t(`${msgKey}.messages.url`)}
              status="ok"
              contextMenu={(
                <DeliverableMediaRowContextMenu
                  subcampaign={subcampaign}
                  deliverable={deliverable}
                  canUpload={canUpload}
                  onUploadModeToggled={onUploadModeToggled}
                />
              )}
              content={(
                <DeliverableMediaLink
                  post={post}
                  onDeletePost={canUpload ? onDeletePost : undefined}
                />
              )}
            /> :
            this.renderForm()
        }
        <DeliverableMediaRow
          canUpload={canUpload}
          title={__t(`${msgKey}.labels.files`)}
          comment={__t(`${msgKey}.messages.files`)}
          status="ok"
          contextMenu={(
            <DeliverableMediaRowContextMenu
              subcampaign={subcampaign}
              deliverable={deliverable}
              attachments={attachments}
              attachmentType={DELIVERABLE_ATTACHMENT_TYPE_HIGHRES}
              canUpload={canUpload}
              onDownloadAttachments={onDownloadAttachments}
              onUploadModeToggled={onUploadModeToggled}
            />
          )}
          contentCentered={false}
          content={(
            <DeliverableMediaAttachments
              canUpload={canUpload}
              placeholderForUpload={(
                <DeliverableMediaUploader
                  offerId={subcampaign.offer.id}
                  deliverable={deliverable}
                  attachmentType={DELIVERABLE_ATTACHMENT_TYPE_HIGHRES}
                  onUploadModeTracking={() => this.handleUploadTracking('other files')}
                />
              )}
              attachments={attachmentsHighres}
              onDeleteAttachment={onDeleteAttachment}
            />
          )}
        />
      </React.Fragment>
    );
  }
}

export default DeliverableMediaOther;
